/**
 * PayslipPage — Saját bérjegyzékeim (self-service, PAY-10)
 *
 * Év-szűrő + táblázat (időszak / bruttó / nettó / PDF letöltés)
 * RBAC: OPERATOR, TECHNIKUS, BOLTVEZETO, PARTNER_OWNER, SUPER_ADMIN
 */

import { useTranslation } from '@kgc/i18n';
import {
  Card,
  CardContent,
  CardHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@kgc/ui';
import { useState } from 'react';
import { useMyPayslips } from '../hooks/useMyPayslips';

const MONTHS_HU = [
  'Január',
  'Február',
  'Március',
  'Április',
  'Május',
  'Június',
  'Július',
  'Augusztus',
  'Szeptember',
  'Október',
  'November',
  'December',
];

function buildYearOptions(): number[] {
  const currentYear = new Date().getFullYear();
  return [currentYear - 2, currentYear - 1, currentYear];
}

/**
 * A PayrollItem nem tartalmaz year/month direkt — a payrollRunId alapján
 * a createdAt dátumból közelítjük az időszakot.
 * (Pontosabb megoldás: GET /runs/:id, de az külön kérés lenne minden sorhoz.)
 */
function formatPeriod(createdAt: string): string {
  const d = new Date(createdAt);
  const monthLabel = MONTHS_HU[d.getMonth()] ?? String(d.getMonth() + 1);
  return `${d.getFullYear()} ${monthLabel}`;
}

export function PayslipPage() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const yearOptions = buildYearOptions();

  const { data: payslips = [], isLoading, error } = useMyPayslips(year);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('payroll.payslip.myTitle')}</h1>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <p className="text-center text-muted-foreground py-8">{t('common.loading')}</p>
          )}
          {error !== null && !isLoading && (
            <p className="text-center text-destructive py-8">{t('common.error')}</p>
          )}
          {!isLoading && error === null && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('payroll.payslip.period')}</TableHead>
                  <TableHead className="text-right">{t('payroll.payslip.gross')}</TableHead>
                  <TableHead className="text-right">{t('payroll.payslip.net')}</TableHead>
                  <TableHead className="text-right">{t('payroll.payslip.downloadPdf')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payslips.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      {t('payroll.payslip.empty')}
                    </TableCell>
                  </TableRow>
                )}
                {payslips.map(item => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{formatPeriod(item.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      {item.totalGross.toLocaleString('hu-HU')} Ft
                    </TableCell>
                    <TableCell className="text-right">
                      {item.totalNet.toLocaleString('hu-HU')} Ft
                    </TableCell>
                    <TableCell className="text-right">
                      <a
                        href={`/api/v1/payroll/payslip/${item.id}/pdf`}
                        download
                        className="text-primary underline underline-offset-2 hover:opacity-80"
                      >
                        {t('payroll.payslip.downloadPdf')}
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
